import { Inference, Trade } from '@/app/data/api';

// Whether enough inference data has loaded for the trade chart to render, for a given side. Ready once
// every static inference timestamp at/after the first trade has been retrieved.
//
// `tradeCountUsedForStaticInferenceTimes` must match the current trade count; otherwise the static
// timestamps were generated for a different set of trades (or not generated yet, on the initial
// render), so we're not ready.
//
// `requiredTimes` -- the static timestamps at/after the first trade -- can legitimately be empty, e.g.
// when the earliest trade is after the trading-hours cutoff so no in-hours static timestamp falls
// at/after it. There's then nothing to wait for, so this returns true (`every` is vacuously true on an
// empty array) rather than leaving the chart stuck in the loading state forever.
export function isInferenceDataReady(
  trades: Trade[],
  tradeCountUsedForStaticInferenceTimes: number,
  staticTimes: number[],
  inferences: Inference[],
): boolean {
  if (!trades.length || tradeCountUsedForStaticInferenceTimes !== trades.length) {
    return false;
  }
  const earliestTradeReportMs = trades[0].report_date.getTime();
  const requiredTimes = staticTimes.filter(t => t >= earliestTradeReportMs);
  const retrieved = new Set(inferences.map(i => i.date.getTime()));
  return requiredTimes.every(t => retrieved.has(t));
}

// Resolves the trade chart's x-axis [min, max] bounds from the smallest/largest x keys across every
// series. The caller seeds empty series with Number.MAX_SAFE_INTEGER (min) / Number.MIN_SAFE_INTEGER
// (max), so:
//   - minKey > maxKey  => every series was empty: no renderable domain, returns null (caller loads).
//   - minKey === maxKey => the data collapsed to a single distinct x value (e.g. one trade and no
//     in-hours inferences yet). Chart.js can't render a zero-width domain, so widen it by `pad` (in the
//     active axis's key units) and return the centered bounds rather than hanging on the loader.
//   - otherwise         => the keys already span a non-zero range, returned unchanged.
export function resolveXAxisBounds(
  minKey: number,
  maxKey: number,
  pad: number,
): [number, number] | null {
  if (minKey > maxKey) {
    return null;
  }
  if (minKey === maxKey) {
    return [minKey - pad, maxKey + pad];
  }
  return [minKey, maxKey];
}
